/**
 * FairwayBid – Console-Text in Bausteine zerlegen
 *
 * Die Texte auf "So funktioniert es" gehören Tom und stehen in der Console.
 * Damit sie nicht als Wand aus Fettschrift dastehen, zerlegen wir sie hier
 * in ihre Teile und geben sie gestaltet aus. Der Inhalt bleibt seiner – wer
 * ihn in der Console ändert, ändert die Seite.
 *
 * Nachsichtig geschrieben: Passt der Text nicht ins erwartete Muster, kommt
 * er als ein Stück zurück statt gar nicht.
 *
 * Reine Zeichenkettenarbeit, kein React – damit prüfbar.
 */

const ohneFett = t =>
  String(t || '')
    .replace(/\*\*/g, '')
    .trim();

/** Absätze eines Markdown-Textes (Leerzeile trennt). */
export const absaetze = markdown =>
  String(markdown || '')
    .split(/\n\s*\n/)
    .map(a => a.trim())
    .filter(a => a !== '');

/**
 * Zerlegt einen Block der Form
 *
 *   **Überschrift**
 *
 *   Text dazu.
 *
 *   **Nächste Überschrift**
 *   ...
 *
 * in einzelne Notizen. Steht keine Überschrift im Text, kommt eine einzige
 * Notiz ohne Überschrift zurück.
 */
export const parseNotes = (markdown, fallbackHeadline = null) => {
  const teile = absaetze(markdown);
  if (teile.length === 0) {
    return [];
  }

  const istUeberschrift = t => /^\*\*[^*]+\*\*$/.test(t);
  if (!teile.some(istUeberschrift)) {
    return [{ headline: fallbackHeadline, text: teile.join('\n\n') }];
  }

  const notizen = [];
  let aktuell = null;
  for (const teil of teile) {
    if (istUeberschrift(teil)) {
      if (aktuell) notizen.push(aktuell);
      aktuell = { headline: ohneFett(teil), text: '' };
    } else if (aktuell) {
      aktuell.text = aktuell.text ? `${aktuell.text}\n\n${teil}` : teil;
    } else {
      notizen.push({ headline: fallbackHeadline, text: teil });
    }
  }
  if (aktuell) notizen.push(aktuell);
  return notizen;
};

/**
 * Zerlegt eine Tarifkachel aus der Console.
 *
 *   Titel: "Professional · 49,99 € pro Monat"
 *   Text:  "**Für Händler, die regelmäßig anbieten.**
 *
 *           **Alles aus Basis, zusätzlich**
 *
 *           - 15 Angebote pro Monat
 *           - ..."
 */
export const parsePlan = (titel, markdown) => {
  const kopf = String(titel || '').split(/\s*·\s*/);
  const name = ohneFett(kopf[0]) || 'Paket';
  const preis = kopf.length > 1 ? ohneFett(kopf.slice(1).join(' · ')) : '';

  const teile = absaetze(markdown);
  const tagline = teile.length > 0 && !teile[0].startsWith('-') ? ohneFett(teile[0]) : '';

  // Alles, was als Aufzählung beginnt, sind die enthaltenen Leistungen.
  // Beginnt der Text gleich mit einer Aufzählung, gibt es keine Tagline und
  // die erste Zeile darf nicht verloren gehen - deshalb die Fallunterscheidung.
  const punkte = [];
  let umfang = null;
  for (const teil of tagline ? teile.slice(1) : teile) {
    if (teil.startsWith('-')) {
      for (const zeile of teil.split('\n')) {
        const p = zeile.replace(/^\s*-\s*/, '').trim();
        if (p) punkte.push(ohneFett(p));
      }
    } else if (/^\*\*[^*]+\*\*$/.test(teil)) {
      umfang = ohneFett(teil);
    }
  }

  return { name, preis, tagline, umfang, punkte };
};
